import {ScreenFullIcon} from '@primer/octicons-react'
import {Box, IconButton} from '@primer/react'
import type {DialogHeaderProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useCallback, useMemo} from 'react'

import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {issueNewPathFromExisting} from '../utils/urls'
import {noop} from '@github-ui/noop'
import {useKeyPress} from '@github-ui/use-key-press'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS, BUTTON_LABELS} from '../constants'
import type {IssueCreateUrlParams} from '../utils/template-args'

type CreateIssueDialogHeaderProps = {
  showFullScreenButton?: boolean
  createIssueUrlParamsRef?: RefObject<IssueCreateUrlParams>
  navigate: (url: string) => void
} & DialogHeaderProps

export const CreateIssueDialogHeader = ({
  showFullScreenButton,
  createIssueUrlParamsRef,
  navigate,
  dialogLabelId,
  onClose,
}: CreateIssueDialogHeaderProps) => {
  const onCloseClick = useCallback(() => {
    onClose('close-button')
  }, [onClose])

  const pathname = ssrSafeWindow?.location?.pathname ?? ''
  const baseUrlForFullscreenLink = useMemo(() => issueNewPathFromExisting(pathname), [pathname])

  const navigateToFullscreen = () => {
    const templateParams = createIssueUrlParamsRef?.current?.generateUrlParams()
    const link = templateParams ? `${baseUrlForFullscreenLink}?${templateParams}` : baseUrlForFullscreenLink
    navigate(link)
  }

  showFullScreenButton = showFullScreenButton && navigate !== noop

  if (showFullScreenButton) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useKeyPress([HOTKEYS.enter], navigateToFullscreen, {
      metaKey: true,
      shiftKey: true,
      triggerWhenPortalIsActive: true,
    })
  }

  return (
    <Dialog.Header>
      <Box sx={{display: 'flex'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, px: 2, py: '6px'}}>
          <Dialog.Title id={dialogLabelId}>{LABELS.issueCreateDialogTitle}</Dialog.Title>
        </Box>
        {showFullScreenButton && (
          <IconButton
            sx={{pt: '2px', borderRadius: '4px'}}
            variant="invisible"
            onClick={navigateToFullscreen}
            aria-label={BUTTON_LABELS.createFullscreen}
            icon={ScreenFullIcon}
          />
        )}
        <Dialog.CloseButton onClose={onCloseClick} />
      </Box>
    </Dialog.Header>
  )
}

try{ CreateIssueDialogHeader.displayName ||= 'CreateIssueDialogHeader' } catch {}